// Keeping screens current without a manual refresh.
//
// - useLiveRefresh(refresh): re-runs a page's quiet reload when an alert
//   arrives, when you come back to the app after a while, and every 90s
//   while it's on screen.
// - useServiceWorkerBridge(): turns "an alert just arrived" from the service
//   worker into the event the pages listen for.
// - useNewVersion(): notices when a newer dashboard has been deployed.

import { useEffect, useRef, useState } from "react";

export const DATA_EVENT = "bb:data-changed";

export function announceDataChange() {
  window.dispatchEvent(new Event(DATA_EVENT));
}

export function useLiveRefresh(refresh, { everyMs = 90000, afterHiddenMs = 20000 } = {}) {
  const ref = useRef(refresh);
  useEffect(() => {
    ref.current = refresh;
  }, [refresh]);

  useEffect(() => {
    let hiddenAt = document.hidden ? Date.now() : null;
    const run = () => {
      try {
        ref.current?.();
      } catch {
        /* a failed refresh just waits for the next one */
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else {
        if (hiddenAt && Date.now() - hiddenAt >= afterHiddenMs) run();
        hiddenAt = null;
      }
    };
    const timer = setInterval(() => {
      if (!document.hidden) run();
    }, everyMs);
    window.addEventListener(DATA_EVENT, run);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(timer);
      window.removeEventListener(DATA_EVENT, run);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [everyMs, afterHiddenMs]);
}

export function useServiceWorkerBridge() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMessage = (e) => {
      if (e.data?.type === "bb-refresh") announceDataChange();
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);
}

// ---- new version -----------------------------------------------------------

const MAIN_JS = /\/static\/js\/main\.[\w]+\.js/;

function loadedBundle() {
  const s = document.querySelector('script[src*="/static/js/main."]');
  if (!s) return null;
  const m = s.getAttribute("src").match(MAIN_JS);
  return m ? m[0] : null;
}

async function deployedBundle() {
  const res = await fetch("/index.html", { cache: "no-store" });
  if (!res.ok) return null;
  const m = (await res.text()).match(MAIN_JS);
  return m ? m[0] : null;
}

/** A modal (quote builder, log a lead…) may hold unsaved work. */
function somethingOpen() {
  return document.body.style.overflow === "hidden";
}

/**
 * True once a newer build is live. When you come back to the app after 5+
 * minutes away and nothing is open, it simply reloads into the new version.
 */
export function useNewVersion({ autoApply = false } = {}) {
  const [available, setAvailable] = useState(false);
  const availableRef = useRef(false);

  useEffect(() => {
    const current = loadedBundle();
    if (!current) return undefined; // dev server: nothing to compare
    let hiddenAt = null;
    let stopped = false;

    const check = async () => {
      try {
        const latest = await deployedBundle();
        if (!stopped && latest && latest !== current) {
          availableRef.current = true;
          setAvailable(true);
        }
      } catch {
        /* offline: try again later */
      }
    };
    const onVisibility = async () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        return;
      }
      const away = hiddenAt ? Date.now() - hiddenAt : 0;
      hiddenAt = null;
      await check();
      if (autoApply && availableRef.current && away >= 5 * 60000 && !somethingOpen()) {
        window.location.reload();
      }
    };

    const first = setTimeout(check, 10000);
    const timer = setInterval(() => !document.hidden && check(), 5 * 60000);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stopped = true;
      clearTimeout(first);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [autoApply]);

  return available;
}
